// Client docs (Workspace 2, screen 6): every document asked of the client (pbc_index) — counts, a type filter, and one list
// grouped by document type: ref, the document (with its one-line issue, or which workpaper the agent asked for it), which
// workpapers need it, when it came in, three checks (read, verified, filed) and the engine's status. The chat on this page
// is the client-documents conversation. The page follows the engine live (ADR-0032) and says when it last published. A request a
// document already in the vault seems to answer shows "Looks answered by <doc> — confirm" with the engine's reasons (ADR-0033):
// confirming or dismissing is a person's act through the act drawer; nothing is received automatically.
import { Link, useSearchParams } from "react-router-dom";
import type { ActionIntent } from "../../app/ActionDrawer";
import { Button, Icon, LoadingRows, EmptyState, StatusPill } from "../../components";
import type { PbcIndexRow } from "../../data/types";
import { fmtDate } from "../../lib/format";
import { DOC_STATUS_LABEL, groupBy } from "../../lib/workspace";
import { Chips, useWs, useWsAct } from "./common";
import { useApp } from "../../app/context";
import { useLiveTables } from "../../data/live";
import { LiveStamp } from "./LiveStamp";

export function WsClientDocs() {
  const ws = useWs();
  const { ds } = useApp();
  // live (ADR-0032): reload when the engine publishes the client documents (pbc_index), the vault (documents) or the requests
  const live = useLiveTables(ds, ws.id, ["pbc_index", "documents", "pbc_requests"], ws.reload);
  const [params, setParams] = useSearchParams();
  const type = params.get("type") ?? "all";
  const docs = ws.pbc;
  const shown = docs.filter((d) => type === "all" || d.docType === type);
  const n = (f: (d: PbcIndexRow) => boolean) => docs.filter(f).length;
  const late = n((d) => d.status === "late");
  const counts: Array<[string, string]> = [
    ["Requested", String(docs.length)], ["Received", String(n((d) => ["received", "processed", "issue"].includes(d.status)))], ["Processed", String(n((d) => d.status === "processed"))],
    ["Issues", String(n((d) => d.status === "issue"))], ["Outstanding · late", `${n((d) => d.status === "outstanding" || d.status === "late")} · ${late}`],
  ];
  const types = groupBy(docs, (d) => d.docType).map(([t, list]) => ({ id: t, label: list[0].docTypeLabel, count: list.length }));
  const { open, drawer } = useWsAct(ws.reload);
  const answered = docs.filter((d) => (d.looksAnswered ?? []).length > 0 && ["draft", "outstanding", "late"].includes(d.status));

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div><h1 className="text-xl font-semibold">Client docs</h1><p className="text-sm text-muted">What we asked the client for, what came in, and what we did with it.</p></div>
        <LiveStamp live={live} />
      </header>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5" aria-label="Client document counts">
        {counts.map(([k, v]) => <div key={k} className="rounded-lg border border-line bg-raised px-3 py-2"><dt className="text-xs text-muted">{k}</dt><dd className="text-xl font-semibold tabular-nums">{v}</dd></div>)}
      </dl>
      {answered.length > 0 && (
        <p className="rounded-md border border-waiting-line bg-waiting-bg px-3 py-2 text-sm" role="status">
          {answered.length} request{answered.length === 1 ? " looks" : "s look"} answered already by a document in the vault — confirm or dismiss each below.
        </p>
      )}
      <Chips label="Document type" value={type} onChange={(t) => setParams(t === "all" ? {} : { type: t }, { replace: true })}
        options={[{ id: "all", label: "All", count: docs.length }, ...types]} />
      {ws.loading ? <LoadingRows rows={6} label="Loading client documents" /> : shown.length === 0 ? <EmptyState title="No client documents here yet." /> : (
        groupBy(shown, (d) => d.docTypeLabel).map(([g, list]) => (
          <section key={g} aria-label={`${g}, ${list.length}`} className="overflow-hidden rounded-lg border border-line bg-raised">
            <h2 className="border-b border-line bg-sunken px-3 py-1.5 text-xs font-semibold text-muted">{g} · {list.length}</h2>
            <table className="hidden w-full table-fixed text-sm md:table">
              <thead className="text-left text-xs text-muted"><tr>
                <th className="w-20 px-3 py-1.5 font-medium">Ref</th><th className="px-3 py-1.5 font-medium">Document</th><th className="w-28 px-3 py-1.5 font-medium">Needed by</th>
                <th className="w-32 px-3 py-1.5 font-medium">Received</th><th className="w-24 px-3 py-1.5 font-medium">Checks</th><th className="w-28 px-3 py-1.5 font-medium">Status</th></tr></thead>
              <tbody className="divide-y divide-line">
                {list.map((d) => (
                  <tr key={d.id} id={`pbc-${d.id}`} className="align-top">
                    <td className="px-3 py-2 font-mono text-xs">{d.ref}</td>
                    <td className="px-3 py-2"><DocName d={d} open={open} /></td>
                    <td className="px-3 py-2"><NeededBy d={d} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{d.receivedAt ? `${fmtDate(d.receivedAt)} · ${d.files} file${d.files === 1 ? "" : "s"}` : d.due ? `Due ${fmtDate(d.due)}` : "—"}</td>
                    <td className="px-3 py-2"><Checks d={d} /></td>
                    <td className="px-3 py-2"><StatusPill state={d.status} label={DOC_STATUS_LABEL[d.status]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-line md:hidden">
              {list.map((d) => (
                <li key={d.id} id={`pbc-m-${d.id}`} className="space-y-1.5 px-3 py-2.5 text-sm">
                  <div className="flex items-start justify-between gap-2"><span className="font-mono text-xs text-muted">{d.ref}</span><StatusPill state={d.status} label={DOC_STATUS_LABEL[d.status]} /></div>
                  <DocName d={d} open={open} />
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted"><NeededBy d={d} /><Checks d={d} />
                    <span>{d.receivedAt ? `${fmtDate(d.receivedAt)} · ${d.files} file${d.files === 1 ? "" : "s"}` : d.due ? `Due ${fmtDate(d.due)}` : ""}</span></div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {drawer}
    </div>
  );
}

function DocName({ d, open }: { d: PbcIndexRow; open: (i: ActionIntent) => void }) {
  const ws = useWs();
  const m = ["draft", "outstanding", "late"].includes(d.status) ? (d.looksAnswered ?? [])[0] : undefined;
  return (
    <div>
      <p className="font-medium">{d.title}</p>
      {d.issue ? <p className="text-xs text-blocked">{d.issue}</p>
        : d.requestedBy === "agent" && d.neededBy[0] ? <p className="text-xs text-agent"><span aria-hidden="true">✦ </span>Asked by agent for {d.neededBy[0]}</p> : null}
      {m && (
        <div className="mt-1 rounded-md border border-waiting-line bg-waiting-bg px-2 py-1.5 text-xs" aria-label={`Looks answered: ${d.ref}`}>
          <p><span className="font-medium">Looks answered by {m.name}</span> ({m.strength}) — confirm. <span className="text-muted">{m.reasons.join("; ")}.</span></p>
          <p className="mt-1 flex flex-wrap gap-1.5">
            <Button size="sm" kind="primary" data-command={m.confirm.command}
              onClick={() => open({ command: m.confirm.command, engagementId: ws.id, fixed: m.confirm.payload, title: `Confirm ${d.ref} is answered by ${m.name}` })}>Confirm</Button>
            <Button size="sm" data-command={m.dismiss.command}
              onClick={() => open({ command: m.dismiss.command, engagementId: ws.id, fixed: m.dismiss.payload, title: `Not the answer to ${d.ref}: ${m.name}` })}>Dismiss</Button>
          </p>
        </div>
      )}
    </div>
  );
}
function NeededBy({ d }: { d: PbcIndexRow }) {
  const ws = useWs();
  if (!d.neededBy.length) return <span className="text-xs text-muted">—</span>;
  return <span className="flex flex-wrap gap-1">{d.neededBy.map((r) => <Link key={r} className="font-mono text-xs text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(r)}`}>{r}</Link>)}</span>;
}
function Checks({ d }: { d: PbcIndexRow }) {
  const items: Array<[boolean, string]> = [[d.checks.read, "Read"], [d.checks.verified, "Verified"], [d.checks.filed, "Filed"]];
  return (
    <span className="inline-flex gap-1" role="list" aria-label="Checks">
      {items.map(([ok, label]) => (
        <span key={label} role="listitem" aria-label={`${label}: ${ok ? "yes" : "not yet"}`} title={`${label}: ${ok ? "yes" : "not yet"}`}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${ok ? "border-done bg-done text-paper" : "border-line text-muted"}`}>
          {ok ? <Icon name="check" size={12} /> : <span className="text-[9px]">{label[0]}</span>}
        </span>
      ))}
    </span>
  );
}
